package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.first
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.personNameArg
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.datetime.plus
import kotlinx.datetime.number
import kotlinx.datetime.LocalDate
import kotlinx.datetime.DatePeriod
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.legal.DayClosure
import com.evoapps.evoshift.domain.usecase.GetDayClosuresUseCase
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.daily.UpdateDailyTargetUseCase
import com.evoapps.evoshift.domain.usecase.daily.SubmitDailyRevenueUseCase
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase
import com.evoapps.evoshift.domain.usecase.finance.GetStaffingHintsUseCase
import com.evoapps.evoshift.domain.usecase.finance.StaffingContext
import com.evoapps.evoshift.domain.staffing.DayStaffingHint
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.shift.CopyPlannedTimesToActualUseCase
import com.evoapps.evoshift.domain.usecase.shift.PlannedTimesCopy
import com.evoapps.evoshift.domain.usecase.shift.withPlannedAsActual
import com.evoapps.evoshift.domain.usecase.shift.hasActualTime
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.copy_plan_done
import evoshift.composeapp.generated.resources.copy_plan_nothing
import evoshift.composeapp.generated.resources.forecast_accepted
import com.evoapps.evoshift.domain.forecast.ForecastStatus
import com.evoapps.evoshift.domain.usecase.finance.DaySuggestion
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.asksWhoWorked
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.usecase.finance.DailyRecordInputs
import com.evoapps.evoshift.domain.usecase.finance.DailyRevenueInput
import com.evoapps.evoshift.domain.usecase.shift.RecordShiftWorkerUseCase
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import com.evoapps.evoshift.domain.usecase.user.PersonNames
import com.evoapps.evoshift.domain.usecase.user.ResolvePersonNamesUseCase
import evoshift.composeapp.generated.resources.who_worked_missing_rates
import evoshift.composeapp.generated.resources.who_worked_nobody_done
import evoshift.composeapp.generated.resources.who_worked_recorded
import evoshift.composeapp.generated.resources.actual_work_absence_failed
import evoshift.composeapp.generated.resources.actual_work_saved_absence
import evoshift.composeapp.generated.resources.actual_work_saved_nobody
import evoshift.composeapp.generated.resources.actual_work_saved_planned
import evoshift.composeapp.generated.resources.split_absence_failed
import evoshift.composeapp.generated.resources.split_done
import evoshift.composeapp.generated.resources.split_undone
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.ActualWorker
import com.evoapps.evoshift.domain.model.ShiftSplits
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.model.isNoShow
import com.evoapps.evoshift.domain.usecase.shift.RecordActualWorkUseCase
import com.evoapps.evoshift.domain.usecase.shift.SplitShiftUseCase
import com.evoapps.evoshift.domain.usecase.shift.UndoShiftSplitUseCase
import kotlinx.datetime.DayOfWeek


/**
 * SH-46: copying the planned times over [clocked] shifts that already have their own times waits
 * for the manager's answer: overwrite them, copy only to the shifts without times, or cancel.
 */
data class CopyPlanConfirmation(
    val date: String,
    /** One shift's copy, or null for the whole day. */
    val shiftId: String?,
    val clocked: Int,
    /** Shifts without times the copy would fill anyway. */
    val empty: Int
)

data class DailyRecordsUiState(
    val workplace: Workplace? = null,
    val roster: Roster? = null,
    val records: Map<String, DailyRecord> = emptyMap(),
    val isLoading: Boolean = false,
    /** What the person may do at this workplace: the daily money needs VIEW_FINANCES. */
    val capabilities: Set<Capability> = emptySet(),
    /** Today (ISO), where entering results starts when nothing earlier is missing. */
    val today: String? = null,
    /** SH-46: the month's shifts, for copying the planned times into the actual ones. */
    val shifts: List<Shift> = emptyList(),
    /** SH-46: a copy waiting for the manager to say whether clocked times are overwritten. */
    val copyConfirmation: CopyPlanConfirmation? = null,
    val error: UiText? = null,
    /** SH-168: the month could not be read at all: the screen offers Try again instead of an empty month. */
    val loadError: UiText? = null,
    /**
     * SH-168: the notes under the title (amounts are net, labour cost is an estimate) folded behind
     * the info button: open on the first visit only, folded once dismissed (kept on the device).
     */
    val notesFolded: Boolean = false,
    /** A save that went through (shown once, then [DailyRecordsEvent.MessageShown]). */
    val message: UiText? = null,
    /** SH-55: the days the workplace's Sunday policy closes, by ISO date. */
    val closedDays: Map<String, DayClosure> = emptyMap(),
    /** SH-54: the labour target, last month's takings and the closed days the hints need. */
    val staffingContext: StaffingContext? = null,
    /** SH-128: the workplace's active staff, to say who worked an open shift. */
    val staff: List<User> = emptyList(),
    /**
     * SH-128: days recomputed from figures typed but not saved yet (takings, plan), by ISO date:
     * the labour % and the staffing hint shown as a preview until the day is saved.
     */
    val previews: Map<String, DailyRecord> = emptyMap(),
    /** SH-152: the team's names by user id, for the figures' explanations. */
    val names: Map<String, String> = emptyMap(),
    /**
     * SH-195: the month's ledger (recurring and one-off costs and revenue items), for the daily
     * results: each day counts its share of the recurring ones ([resultDay]).
     */
    val ledger: List<com.evoapps.evoshift.domain.model.FixedCost> = emptyList(),
    /** SH-226: each day's weather by ISO date (empty when the workplace has weather off). */
    val weather: Map<String, com.evoapps.evoshift.domain.model.DayWeather> = emptyMap(),
    /** SH-227: each day's events and special days by ISO date (empty: none). */
    val specialDays: Map<String, com.evoapps.evoshift.domain.model.DaySpecials> = emptyMap(),
    /** SH-236: the actions on the day's shifts that are offered (the use cases bound, see the view model). */
    val actualWorkOffered: Boolean = false,
    /** SH-236: splitting a shift and undoing a split are offered. */
    val splitOffered: Boolean = false,
    /** SH-257: the local time now ("YYYY-MM-DDTHH:mm:ss"): today's shifts that are over wait for confirming. */
    val nowLocal: String = ""
) {
    /**
     * SH-257: the day's hours at a glance for the day's one line ("Godziny: 5 zmian, 2 do
     * potwierdzenia →"), for those who may open Godziny pracy; null otherwise.
     */
    fun hoursSummary(date: String): com.evoapps.evoshift.domain.model.WorkHoursDaySummary? {
        val day = today ?: return null
        if (!capabilities.shows(Gate.WORK_HOURS)) return null
        return com.evoapps.evoshift.domain.model.WorkHoursRules.summary(shifts, date, day, nowLocal.ifEmpty { "${day}T00:00:00" })
    }

    /** SH-226: the sources of the weather on screen, for the attribution line. */
    val weatherSources: Set<com.evoapps.evoshift.domain.model.WeatherSource>
        get() = com.evoapps.evoshift.domain.model.DayWeather.sourcesOf(weather.values)

    /** SH-195: [record] as its daily result counts it (DayResults.resultDay); the stored record is unchanged. */
    fun resultDay(record: DailyRecord): DailyRecord = com.evoapps.evoshift.domain.model.DayResults.resultDay(record, ledger)

    /** SH-128: managers who edit both the rosters and the money record who worked an open shift. */
    val mayRecordWorkers: Boolean
        get() = capabilities.shows(Gate.ROSTER_EDITING) && capabilities.shows(Gate.MONEY_EDITING)

    /** SH-236: managers who may record who worked a day's shifts (and split them) on a day that is open. */
    fun mayRecordActualWork(date: String): Boolean =
        mayRecordWorkers && actualWorkOffered && records[date]?.isLocked != true && today != null && date <= today

    /** SH-236: as [mayRecordActualWork], for splitting and undoing a split. */
    fun maySplit(date: String): Boolean = mayRecordActualWork(date) && splitOffered

    /** SH-128: [date]'s day as typed (its preview) or as saved. */
    fun shownRecord(date: String): DailyRecord? = previews[date] ?: records[date]

    /**
     * SH-128: the staffing hint of [date] from the figures typed (its preview) when there is one,
     * else as saved.
     */
    fun staffingHintOf(date: String): DayStaffingHint? {
        val preview = previews[date] ?: return staffingHints[date]
        return staffingContext?.hintsOf((records + (date to preview)).values)?.days?.firstOrNull { it.date.toString() == date }
    }

    /**
     * SH-128: weekends and public holidays of the workplace's market (Poland's legal calendar by
     * default), by ISO date: their rows are tinted in the table.
     */
    val restDays: Set<String> by lazy {
        val rules = PayrollRules.of(workplace?.settings?.market ?: Market.POLAND)
        records.keys.filter { iso ->
            val day = runCatching { LocalDate.parse(iso) }.getOrNull() ?: return@filter false
            day.dayOfWeek == DayOfWeek.SATURDAY || day.dayOfWeek == DayOfWeek.SUNDAY || rules.isHoliday(day)
        }.toSet()
    }

    /**
     * SH-54: each day's staffing hint by ISO date, from the records as they are now (a changed
     * target or the takings show in the hint at once); computed once per state.
     */
    val staffingHints: Map<String, DayStaffingHint> by lazy {
        staffingContext?.hintsOf(records.values)?.days?.associateBy { it.date.toString() }.orEmpty()
    }

    /**
     * SH-120: each day's suggested target from the sales history, by ISO date; empty without
     * enough history (nothing is shown then) or without money.
     */
    val suggestions: Map<String, DaySuggestion> by lazy {
        staffingContext?.forecast?.takeIf { it.status == ForecastStatus.READY }
            ?.days?.associateBy { it.date.toString() }.orEmpty()
    }

    /** SH-120: the suggestions a manager could still accept: open, unlocked days whose target differs. */
    val pendingSuggestions: List<DaySuggestion>
        get() = suggestions.values.filter { s ->
            val record = records[s.date.toString()]
            record != null && !record.isLocked && s.date.toString() !in closedDays && record.revenue.planNetto != s.amount
        }.sortedBy { it.date }

    /**
     * The worked shifts of [date] (assigned, or SH-128 with the person who worked them recorded),
     * named after who worked them, by planned start (the day dialog's list). SH-236: an assigned
     * shift nobody came to stays listed (under the planned person) so the answer can be changed.
     * SH-235: named from [names] (the shared resolver), so nobody on the list is blank.
     */
    fun shiftsOf(date: String): List<Shift> =
        shifts.filter { it.date == date && (it.workerId != null || it.isNoShow) }
            .map { shift ->
                val shown = shift.workerId ?: shift.userId ?: return@map shift
                val name = names[shown] ?: staff.firstOrNull { it.uid == shown }?.displayName
                    ?: shift.userDisplayName.takeIf { shown == shift.userId }
                if (name == shift.userDisplayName) shift else shift.copy(userDisplayName = name)
            }
            .sortedBy { it.plannedTime.start }

    /**
     * SH-236: a person's name for the day's lines and dialogs: from [names] (the shared resolver:
     * the team, then the profiles of people from other workplaces or who left), else the team;
     * SH-237: null when nobody could name them (the screen shows its label, never the id).
     */
    fun nameOf(uid: String): String? =
        (names[uid] ?: staff.firstOrNull { it.uid == uid }?.displayName)?.takeIf { it.isNotBlank() }

    /** SH-128: the open shifts of [date] (today or earlier) nobody has said who worked yet. */
    fun shiftsAskingWhoWorked(date: String): List<Shift> {
        val upTo = today ?: return emptyList()
        return shifts.filter { it.date == date && it.asksWhoWorked(upTo) }.sortedBy { it.plannedTime.start }
    }

    /**
     * SH-71: the day the results carousel opens on: the first day up to today still without
     * takings (and not closed); else today if it is in this month; else the month's first day.
     */
    val entryStartDate: String?
        get() {
            val days = records.keys.sorted()
            if (days.isEmpty()) return null
            val upToToday = days.filter { today == null || it <= today }
            return upToToday.firstOrNull { day -> records[day]?.let { it.revenue.realNetto == null && !it.isLocked } == true }
                ?: today?.takeIf { it in records }
                ?: days.first()
        }
}

sealed class DailyRecordsEvent {
    data class UpdateActuals(
        val date: String,
        /** The day's takings, net (SH-70: revenue is always net). */
        val netto: Double?,
        val plan: Double?,
        val comments: String?
    ) : DailyRecordsEvent()
    /**
     * SH-163: the day's typed figures saved and the day locked (reconciled) in the same write,
     * after the screen asked. A locked day can no longer be edited until it is reopened.
     */
    data class SaveAndLockDay(
        val date: String,
        val netto: Double?,
        val plan: Double?,
        val comments: String?
    ) : DailyRecordsEvent()
    /** SH-163: a locked day opened again for corrections (by whoever may lock it). */
    data class UnlockDay(val date: String) : DailyRecordsEvent()
    /** SH-71: the day's takings (net) from the results carousel; the plan and comments stay. */
    data class SubmitTakings(val date: String, val netto: Double) : DailyRecordsEvent()
    /**
     * SH-76: the day's planned revenue (net) differs from its weekday's sales target; null goes
     * back to that target. The planned labour % follows.
     */
    data class UpdateTarget(val date: String, val target: Double?) : DailyRecordsEvent()
    /** SH-120: every pending suggested target becomes the day's target (editable afterwards). */
    data object AcceptSuggestedTargets : DailyRecordsEvent()
    /**
     * SH-46: the planned times copied into the actual ones, for the whole day or one shift
     * ([shiftId]). Shifts already clocked are not overwritten unless [overwrite]; when there are
     * some and [overwrite] is null, the screen asks first ([DailyRecordsUiState.copyConfirmation]).
     */
    data class CopyPlannedToActual(val date: String, val shiftId: String? = null, val overwrite: Boolean? = null) : DailyRecordsEvent()
    /** The answer to [DailyRecordsUiState.copyConfirmation]: overwrite, only empty ones, or null to cancel. */
    data class ResolveCopy(val overwrite: Boolean?) : DailyRecordsEvent()
    /**
     * SH-128: who worked an open shift of a past day ([workerId]), or null: nobody worked it. Its
     * labour cost and the day's actual figures follow.
     */
    data class RecordWorker(val shiftId: String, val workerId: String?) : DailyRecordsEvent()
    /**
     * SH-236: who actually worked [shiftId] (as planned, someone else, nobody came), with the
     * actual times ([actual]; null keeps the clocked ones) and, for a no-show, the planned
     * person's absence that day ([absenceType]).
     */
    data class RecordActualWork(
        val shiftId: String,
        val worker: ActualWorker,
        val actual: TimeRange?,
        val absenceType: AbsenceType? = null
    ) : DailyRecordsEvent()
    /** SH-236: [shiftId] split at [handoverAt]: [replacementId] takes over until the end. */
    data class SplitShift(
        val shiftId: String,
        val handoverAt: String,
        val replacementId: String,
        val reason: String?,
        val sickness: Boolean,
        val recordSickLeave: Boolean
    ) : DailyRecordsEvent()
    /** SH-236: the split of [shiftId] (either part) undone. */
    data class UndoSplit(val shiftId: String) : DailyRecordsEvent()
    /**
     * SH-128: the day recalculated from figures typed but not saved ([netto] takings, [plan]
     * target; null: as saved), shown as a preview ([DailyRecordsUiState.previews]).
     */
    data class Preview(val date: String, val netto: Double?, val plan: Double? = null) : DailyRecordsEvent()
    /** SH-128: the typed figures were dropped (the dialog or card closed): no preview. */
    data class ClearPreview(val date: String) : DailyRecordsEvent()
    data object MessageShown : DailyRecordsEvent()
    /** SH-168: the month read again after it failed to load. */
    data object Retry : DailyRecordsEvent()
    /** SH-257: back on the screen (from Godziny pracy): the month's shifts and days read again. */
    data object RefreshShifts : DailyRecordsEvent()
    /** SH-168: the notes folded behind the info button (remembered on the device) or opened again. */
    data class SetNotesFolded(val folded: Boolean) : DailyRecordsEvent()
}

/** SH-236: the id a split's second part shows under until it is written. */
const val PENDING_SPLIT_PREFIX = "pending-split-"

/** SH-168: the device remembers that the daily results' notes were folded. */
const val DAILY_NOTES_FOLDED_KEY = "daily_records_notes_folded"


class DailyRecordsViewModel(
    private val workplaceId: String,
    private val rosterId: String,
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository,
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val updateDailyTargetUseCase: UpdateDailyTargetUseCase = UpdateDailyTargetUseCase(financialRepository, calculateDailyRecordUseCase),
    private val submitDailyRevenueUseCase: SubmitDailyRevenueUseCase = SubmitDailyRevenueUseCase(financialRepository, calculateDailyRecordUseCase),
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) },
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    /** SH-46: the month's shifts and the copy of planned times; without them there is no copying. */
    private val shiftRepository: ShiftRepository? = null,
    private val copyPlannedTimes: CopyPlannedTimesToActualUseCase? = null,
    /** SH-55: the closed days the log marks; without Firestore, the calendar shipped with the app. */
    private val getDayClosures: GetDayClosuresUseCase = GetDayClosuresUseCase(),
    /** SH-54: the context of the days' staffing hints; without it there are none. */
    private val getStaffingHints: GetStaffingHintsUseCase? = null,
    /** SH-128: recording who worked an open shift; without it nobody is asked. */
    private val recordShiftWorker: RecordShiftWorkerUseCase? = null,
    private val getAssignableEmployees: GetAssignableEmployeesUseCase? = null,
    /** SH-235: names everyone on the month's shifts; without it, only the team is named. */
    private val resolvePersonNames: ResolvePersonNamesUseCase? = null,
    /** SH-148: the app's saves: a write started here finishes even if the screen is left. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-168: where folding the notes is remembered; without it they open on every visit. */
    private val localSettings: com.evoapps.evoshift.settings.LocalSettings? = null,
    /** SH-226: the month's weather; without it (or with weather off) the days show none. */
    private val observeWeather: com.evoapps.evoshift.domain.usecase.weather.ObserveWorkplaceWeatherUseCase? = null,
    /** SH-227: the month's events and special days; without it the days show none. */
    private val observeSpecialDays: com.evoapps.evoshift.domain.usecase.specialdays.ObserveSpecialDaysUseCase? = null,
    /** SH-236: who actually worked a shift, splitting one and undoing a split; without them not offered. */
    private val recordActualWork: RecordActualWorkUseCase? = null,
    private val splitShift: SplitShiftUseCase? = null,
    private val undoShiftSplit: UndoShiftSplitUseCase? = null
) : ViewModel() {

    private var specialDaysJob: kotlinx.coroutines.Job? = null

    /** SH-227: follows the month's events (typed in) and special days; restarted with the month's data. */
    private fun followSpecialDays(workplace: Workplace, roster: Roster) {
        val observe = observeSpecialDays ?: return
        specialDaysJob?.cancel()
        val first = LocalDate(roster.year, roster.month, 1)
        val last = first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1))
        specialDaysJob = viewModelScope.launch {
            observe(workplace, first, last).collect { snapshot ->
                _uiState.update { state -> state.copy(specialDays = snapshot.days.mapKeys { it.key.toString() }) }
            }
        }
    }

    private var weatherJob: kotlinx.coroutines.Job? = null

    /** SH-226: follows the month's weather (written by the server); restarted with the month's data. */
    private fun followWeather(workplace: Workplace, roster: Roster) {
        val observe = observeWeather ?: return
        weatherJob?.cancel()
        val first = LocalDate(roster.year, roster.month, 1)
        val last = first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1))
        weatherJob = viewModelScope.launch {
            observe(workplace, first, last).collect { days ->
                _uiState.update { state -> state.copy(weather = days.mapKeys { it.key.toString() }) }
            }
        }
    }

    private var editorUserId: String? = null

    /** SH-128: the month's inputs the previews of typed figures are computed from. */
    private var inputs: DailyRecordInputs? = null

    /**
     * SH-152: [record]'s day worked out now from the month's shifts and cost snapshots, keeping
     * its entered figures: who its labour cost is, for the explanation of a saved day (which
     * holds only the totals). Null before the month's data is loaded.
     */
    fun freshDay(record: DailyRecord): DailyRecord? = inputs?.let {
        calculateDailyRecordUseCase.compute(workplaceId, rosterId, record.date, it, DailyRevenueInput.keptFrom(record))
    }

    private val _uiState = MutableStateFlow(
        DailyRecordsUiState(
            notesFolded = localSettings?.getString(DAILY_NOTES_FOLDED_KEY) == "1",
            actualWorkOffered = recordActualWork != null,
            splitOffered = splitShift != null && undoShiftSplit != null
        )
    )
    val uiState: StateFlow<DailyRecordsUiState> = _uiState.asStateFlow()

    init {
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = it.records.isEmpty()) }
            editorUserId = getCurrentUserUseCase().first()?.uid
            val capabilities = capabilitiesAt(getCurrentUserUseCase(), observePlanActive, workplaceId).first()
            _uiState.update { it.copy(capabilities = capabilities, today = today().toString(), nowLocal = localNow()) }
            // Takings and labour cost are the owner's and financial managers': nothing is read otherwise.
            if (!capabilities.shows(Gate.MONEY)) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            try {
                val workplace = workplaceRepository.getWorkplace(workplaceId).getOrThrow()
                val roster = rosterRepository.getRoster(rosterId).getOrThrow()

                // 1. Get existing records from DB
                val existingRecords = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow()
                val recordsMap = existingRecords.associateBy { it.date }.toMutableMap()

                // 2. Days without a stored record show their plan, computed from one load of the
                //    month's data (not one load per day).
                val inputs = calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrNull()
                this@DailyRecordsViewModel.inputs = inputs
                if (inputs != null) {
                    var date = LocalDate(roster.year, roster.month, 1)
                    while (date.month.number == roster.month) {
                        val iso = date.toString()
                        if (!recordsMap.containsKey(iso)) {
                            recordsMap[iso] = calculateDailyRecordUseCase.compute(workplaceId, rosterId, iso, inputs)
                        }
                        date = date.plus(DatePeriod(days = 1))
                    }
                }

                // SH-46: the shifts whose planned times can be copied into the actual ones.
                val shifts = shiftRepository?.getShiftsForRoster(rosterId)?.getOrNull().orEmpty()
                // SH-55: closed by law (or by the owner's choice) on these days.
                val closed = getDayClosures(workplace.settings, roster.year, roster.month).mapKeys { it.key.toString() }
                // SH-54: the hints' target and history; without them the log simply has no hints.
                val staffing = getStaffingHints?.context(rosterId)?.getOrNull()
                // SH-128: who could have worked an open shift (only for those who may say so).
                val team = getAssignableEmployees?.invoke(workplaceId)?.getOrNull()
                val staff = if (capabilities.shows(Gate.ROSTER_EDITING) && capabilities.shows(Gate.MONEY_EDITING)) {
                    team.orEmpty().sortedBy { it.displayName.lowercase() }
                } else emptyList()
                // SH-152: names for the explanations (money only, checked above). SH-235: and for
                // the day's shifts, from the shared resolver: the team, then the profile of anyone
                // else on the month (ended here, working at several workplaces).
                val names = resolvePersonNames?.let { resolve -> resolve(workplaceId, resolve.peopleOn(shifts), team) }
                    ?: team.orEmpty().associate { it.uid to it.displayName }

                _uiState.update { it.copy(
                    workplace = workplace,
                    roster = roster,
                    records = recordsMap.toList().sortedBy { it.first }.toMap(),
                    shifts = PersonNames.named(shifts, names),
                    closedDays = closed,
                    staffingContext = staffing,
                    staff = staff,
                    names = names,
                    ledger = inputs?.fixedCosts.orEmpty(),
                    isLoading = false
                ) }
                _uiState.update { it.copy(loadError = null) }
                loaded = true
                followWeather(workplace, roster)
                followSpecialDays(workplace, roster)
            } catch (e: kotlinx.coroutines.CancellationException) {
                throw e
            } catch (e: Exception) {
                // SH-168: nothing to show is a load error (Try again); over a month on screen, a snackbar.
                _uiState.update {
                    if (it.records.isEmpty()) it.copy(isLoading = false, loadError = e.toUiText())
                    else it.copy(isLoading = false, error = e.toUiText())
                }
            }
        }
    }

    fun onEvent(event: DailyRecordsEvent) {
        if (event == DailyRecordsEvent.MessageShown) {
            _uiState.update { it.copy(message = null) }
            return
        }
        if (event == DailyRecordsEvent.Retry) {
            _uiState.update { it.copy(loadError = null, error = null, isLoading = it.records.isEmpty()) }
            loadData()
            return
        }
        if (event == DailyRecordsEvent.RefreshShifts) {
            refreshShifts()
            return
        }
        if (event is DailyRecordsEvent.SetNotesFolded) {
            if (event.folded) localSettings?.putString(DAILY_NOTES_FOLDED_KEY, "1")
            _uiState.update { it.copy(notesFolded = event.folded) }
            return
        }
        // SH-128: previews change nothing stored, so they are not gated like the saves.
        if (event is DailyRecordsEvent.Preview) return preview(event)
        if (event is DailyRecordsEvent.ClearPreview) {
            _uiState.update { it.copy(previews = it.previews - event.date) }
            return
        }
        // Entering takings: not once the workplace's plan ended (read-only, SH-65).
        if (!_uiState.value.capabilities.shows(Gate.MONEY_EDITING)) return
        when (event) {
            DailyRecordsEvent.MessageShown, DailyRecordsEvent.Retry, DailyRecordsEvent.RefreshShifts, is DailyRecordsEvent.SetNotesFolded,
            is DailyRecordsEvent.Preview, is DailyRecordsEvent.ClearPreview -> Unit
            is DailyRecordsEvent.RecordWorker -> recordWorker(event.shiftId, event.workerId)
            is DailyRecordsEvent.RecordActualWork -> recordActualWork(event)
            is DailyRecordsEvent.SplitShift -> splitShift(event)
            is DailyRecordsEvent.UndoSplit -> undoSplit(event.shiftId)
            is DailyRecordsEvent.CopyPlannedToActual -> copyPlannedToActual(event.date, event.shiftId, event.overwrite)
            is DailyRecordsEvent.ResolveCopy -> {
                val pending = _uiState.value.copyConfirmation ?: return
                _uiState.update { it.copy(copyConfirmation = null) }
                if (event.overwrite != null) copyPlannedToActual(pending.date, pending.shiftId, event.overwrite)
            }
            is DailyRecordsEvent.UpdateActuals -> saveActuals(event.date, event.netto, event.plan, event.comments, lock = false)
            is DailyRecordsEvent.SaveAndLockDay -> saveActuals(event.date, event.netto, event.plan, event.comments, lock = true)
            is DailyRecordsEvent.UnlockDay -> {
                val record = _uiState.value.records[event.date] ?: return
                if (!record.isLocked) return
                _uiState.update { it.copy(records = it.records + (event.date to record.copy(isLocked = false))) }
                viewModelScope.launch {
                    saves.run(dayKey(event.date), queue = true) { financialRepository.unlockDailyRecord(record.id) }
                        .onFailure { e -> rollBack(event.date, record, e) }
                }
            }
            is DailyRecordsEvent.UpdateTarget -> {
                val current = _uiState.value.records[event.date] ?: return
                if (current.isLocked) return
                // A typed target shows at once with its planned labour %; going back to the weekday's
                // target waits for the calculation (the screen does not know that target).
                event.target?.let { target ->
                    _uiState.update { it.copy(records = it.records + (event.date to current.withTarget(target))) }
                }
                viewModelScope.launch {
                    saves.run(dayKey(event.date), queue = true) { updateDailyTargetUseCase(workplaceId, rosterId, event.date, event.target) }.fold(
                        onSuccess = { record -> _uiState.update { it.copy(records = it.records + (event.date to record)) } },
                        onFailure = { e -> rollBack(event.date, current, e) }
                    )
                }
            }
            DailyRecordsEvent.AcceptSuggestedTargets -> acceptSuggestedTargets()
            is DailyRecordsEvent.SubmitTakings -> {
                val current = _uiState.value.records[event.date] ?: return
                if (current.isLocked || event.netto < 0) return
                // The takings show at once; the labour % follows from the calculation.
                _uiState.update { it.copy(records = it.records + (event.date to current.copy(revenue = current.revenue.copy(realNetto = event.netto)))) }
                viewModelScope.launch {
                    saves.run(dayKey(event.date), queue = true) { submitDailyRevenueUseCase(workplaceId, rosterId, event.date, event.netto) }.fold(
                        onSuccess = { record ->
                            AppTelemetry.log(AnalyticsEvent.DailyResultEntered)
                            com.evoapps.evoshift.review.AppReviewPrompt.onSuccess(com.evoapps.evoshift.domain.review.SuccessMoment.TAKINGS_ENTERED, workplaceId)
                            _uiState.update { it.copy(records = it.records + (event.date to record), previews = it.previews - event.date) }
                        },
                        onFailure = { e -> rollBack(event.date, current, e) }
                    )
                }
            }
        }
    }

    /**
     * The day's typed figures: they show at once, the computed totals follow from the calculation.
     * SH-163: with [lock] the day is saved locked in the same write, so nothing typed is lost.
     */
    private fun saveActuals(date: String, netto: Double?, plan: Double?, comments: String?, lock: Boolean) {
        // A locked (reconciled) day is final until it is reopened.
        val current = _uiState.value.records[date]
        if (current?.isLocked == true) return
        if (current != null) {
            val typed = current.copy(
                revenue = current.revenue.copy(
                    realNetto = netto,
                    planNetto = plan ?: current.revenue.planNetto,
                    comments = comments
                ),
                isLocked = lock
            )
            _uiState.update { it.copy(records = it.records + (date to typed)) }
        }
        viewModelScope.launch {
            // SH-148: the day's figures are saved even if the screen is left (in order).
            saves.run(dayKey(date), queue = true) {
                calculateDailyRecordUseCase(
                    workplaceId = workplaceId,
                    rosterId = rosterId,
                    date = date,
                    // A gross figure from before SH-70 stays as it was; none is entered any more.
                    actualBrutto = current?.revenue?.realBrutto,
                    actualNetto = netto,
                    planNetto = plan,
                    comments = comments
                // The roster totals follow on the server (recalculateRosterFinancials).
                ).mapCatching { record ->
                    record.copy(isLocked = lock).also { financialRepository.saveDailyRecord(it).getOrThrow() }
                }
            }.fold(
                onSuccess = { record ->
                    AppTelemetry.log(AnalyticsEvent.DailyResultEntered)
                    // SH-240: a success moment for the store rating prompt (only a day with takings).
                    if (netto != null) com.evoapps.evoshift.review.AppReviewPrompt.onSuccess(com.evoapps.evoshift.domain.review.SuccessMoment.TAKINGS_ENTERED, workplaceId)
                    _uiState.update { it.copy(records = it.records + (date to record), previews = it.previews - date) }
                },
                onFailure = { e -> rollBack(date, current, e) }
            )
        }
    }

    /**
     * SH-46: optimistic: the copied times show at once; the day's actual labour follows from the
     * recalculation. A failed copy puts the shifts back and says why.
     */
    private fun copyPlannedToActual(date: String, shiftId: String?, overwrite: Boolean?) {
        val useCase = copyPlannedTimes ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        if (state.records[date]?.isLocked == true) return
        val plan = PlannedTimesCopy.of(state.shifts, date, shiftId?.let { setOf(it) })
        if (overwrite == null && plan.clocked.isNotEmpty()) {
            _uiState.update { it.copy(copyConfirmation = CopyPlanConfirmation(date, shiftId, plan.clocked.size, plan.empty.size)) }
            return
        }
        val targets = plan.targets(overwrite == true)
        if (targets.isEmpty()) {
            _uiState.update { it.copy(message = UiText.Resource(Res.string.copy_plan_nothing)) }
            return
        }
        val before = state.shifts
        val beforeRecord = state.records[date]
        val copiedIds = targets.map { it.id }.toSet()
        _uiState.update {
            it.copy(shifts = it.shifts.map { s -> if (s.id in copiedIds) s.withPlannedAsActual() else s })
        }
        viewModelScope.launch {
            useCase(workplaceId, rosterId, date, shiftId?.let { setOf(it) }, overwrite == true, editor).fold(
                onSuccess = { outcome ->
                    AppTelemetry.log(AnalyticsEvent.PlannedTimesCopied)
                    refreshInputs()
                    _uiState.update {
                        it.copy(
                            records = outcome.record?.let { record -> it.records + (date to record) } ?: it.records,
                            message = UiText.Resource(Res.string.copy_plan_done, outcome.copied.size)
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(shifts = before) }
                    rollBack(date, beforeRecord, e)
                }
            )
        }
    }

    /**
     * SH-128: optimistic: the shift shows its person (or "nobody worked") and stops asking at once;
     * the cost and the day's figures follow from the recalculation. A failure puts it back.
     */
    private fun recordWorker(shiftId: String, workerId: String?) {
        val useCase = recordShiftWorker ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        if (!state.mayRecordWorkers) return
        val shift = state.shifts.firstOrNull { it.id == shiftId } ?: return
        if (state.records[shift.date]?.isLocked == true) return
        if (workerId != null && state.staff.none { it.uid == workerId }) return
        val before = state.shifts
        val beforeRecord = state.records[shift.date]
        val shown = shift.copy(
            actualUserId = workerId,
            notWorked = workerId == null,
            actualTime = if (workerId != null && !shift.hasActualTime) shift.plannedTime else shift.actualTime
        )
        _uiState.update { it.copy(shifts = it.shifts.map { s -> if (s.id == shiftId) shown else s }) }
        viewModelScope.launch {
            useCase(workplaceId, rosterId, shiftId, workerId, editor).fold(
                onSuccess = { outcome ->
                    refreshInputs()
                    val name = workerId?.let { id -> state.staff.firstOrNull { it.uid == id }?.displayName }
                    _uiState.update {
                        it.copy(
                            shifts = it.shifts.map { s -> if (s.id == shiftId) outcome.shift else s },
                            records = outcome.record?.let { record -> it.records + (shift.date to record) } ?: it.records,
                            message = when {
                                workerId == null -> UiText.Resource(Res.string.who_worked_nobody_done)
                                outcome.missingPayRates -> UiText.Resource(Res.string.who_worked_missing_rates, name.orEmpty())
                                else -> UiText.Resource(Res.string.who_worked_recorded, name.orEmpty())
                            }
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(shifts = before) }
                    rollBack(shift.date, beforeRecord, e)
                }
            )
        }
    }

    /**
     * SH-236: optimistic: the shift shows who worked it (or that nobody came) and its times at once;
     * its cost and the day's figures follow from the recalculation. A failure puts it back.
     */
    private fun recordActualWork(event: DailyRecordsEvent.RecordActualWork) {
        val useCase = recordActualWork ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        val shift = state.shifts.firstOrNull { it.id == event.shiftId } ?: return
        if (!state.mayRecordActualWork(shift.date)) return
        val worker = event.worker
        if (worker is ActualWorker.Person && state.staff.none { it.uid == worker.userId }) return
        if (worker == ActualWorker.AsPlanned && shift.userId == null) return
        val before = state.shifts
        val beforeRecord = state.records[shift.date]
        val nobody = worker is ActualWorker.Nobody
        val shown = shift.copy(
            actualUserId = (worker as? ActualWorker.Person)?.userId?.takeIf { it != shift.userId },
            notWorked = nobody,
            noShowReason = (worker as? ActualWorker.Nobody)?.reason,
            actualTime = when {
                nobody -> null
                event.actual != null -> event.actual
                shift.hasActualTime -> shift.actualTime
                else -> shift.plannedTime
            }
        )
        _uiState.update { it.copy(shifts = it.shifts.map { s -> if (s.id == shift.id) shown else s }) }
        viewModelScope.launch {
            useCase(workplaceId, rosterId, shift.id, worker, event.actual, editor, event.absenceType).fold(
                onSuccess = { outcome ->
                    refreshInputs()
                    val name = personNameArg((worker as? ActualWorker.Person)?.userId?.let { _uiState.value.nameOf(it) })
                    _uiState.update {
                        it.copy(
                            shifts = it.shifts.map { s -> if (s.id == shift.id) outcome.shift else s },
                            records = outcome.record?.let { record -> it.records + (shift.date to record) } ?: it.records,
                            message = when {
                                nobody && outcome.absenceRecorded == true -> UiText.Resource(Res.string.actual_work_saved_absence)
                                nobody && outcome.absenceRecorded == false -> UiText.Resource(Res.string.actual_work_absence_failed)
                                nobody -> UiText.Resource(Res.string.actual_work_saved_nobody)
                                outcome.missingPayRates -> UiText.Resource(Res.string.who_worked_missing_rates, personNameArg(outcome.shift.workerId?.let { id -> it.nameOf(id) }))
                                outcome.shift.actualUserId == null -> UiText.Resource(Res.string.actual_work_saved_planned)
                                else -> UiText.Resource(Res.string.who_worked_recorded, name)
                            }
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(shifts = before) }
                    rollBack(shift.date, beforeRecord, e)
                }
            )
        }
    }

    /**
     * SH-236: optimistic: the two parts show at once (the second under a placeholder id until it
     * is written); the costs and the day's figures follow. A refusal puts the shift back.
     */
    private fun splitShift(event: DailyRecordsEvent.SplitShift) {
        val useCase = splitShift ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        val shift = state.shifts.firstOrNull { it.id == event.shiftId } ?: return
        if (!state.maySplit(shift.date) || state.staff.none { it.uid == event.replacementId }) return
        val replacementName = state.nameOf(event.replacementId)
        val parts = runCatching {
            ShiftSplits.split(
                shift, event.handoverAt, event.replacementId, replacementName, event.reason, event.sickness, editor,
                AppClock.now().toString(), planned = shift.date > (state.today ?: shift.date)
            )
        }.getOrElse { e ->
            _uiState.update { it.copy(error = e.toUiText()) }
            return
        }
        val before = state.shifts
        val beforeRecord = state.records[shift.date]
        val pending = parts.second.copy(id = "$PENDING_SPLIT_PREFIX${shift.id}")
        _uiState.update { it.copy(shifts = it.shifts.map { s -> if (s.id == shift.id) parts.first else s } + pending) }
        viewModelScope.launch {
            useCase(
                workplaceId, rosterId, shift.id, event.handoverAt, event.replacementId, event.reason, event.sickness,
                event.recordSickLeave, editor, state.today ?: shift.date
            ).fold(
                onSuccess = { outcome ->
                    refreshInputs()
                    _uiState.update {
                        it.copy(
                            shifts = it.shifts.mapNotNull { s ->
                                when (s.id) {
                                    shift.id -> outcome.first
                                    pending.id -> outcome.second
                                    else -> s
                                }
                            },
                            records = outcome.record?.let { record -> it.records + (shift.date to record) } ?: it.records,
                            message = if (outcome.absenceRecorded == false) UiText.Resource(Res.string.split_absence_failed)
                            else UiText.Resource(Res.string.split_done, personNameArg(replacementName), event.handoverAt.substringAfter('T').take(5))
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(shifts = before) }
                    rollBack(shift.date, beforeRecord, e)
                }
            )
        }
    }

    /** SH-236: optimistic: the shift shows whole again at once; a refusal brings both parts back. */
    private fun undoSplit(shiftId: String) {
        val useCase = undoShiftSplit ?: return
        val editor = editorUserId ?: return
        val state = _uiState.value
        val shift = state.shifts.firstOrNull { it.id == shiftId } ?: return
        val split = shift.split ?: return
        if (!state.maySplit(shift.date) || shiftId.startsWith(PENDING_SPLIT_PREFIX)) return
        val first = if (split.isFirst) shift else state.shifts.firstOrNull { it.id == split.groupId }
        val second = first?.let { ShiftSplits.otherPart(it, state.shifts) }
        val before = state.shifts
        val beforeRecord = state.records[shift.date]
        if (first != null && second != null) {
            val restored = ShiftSplits.undo(first, second, editor, AppClock.now().toString())
            _uiState.update { it.copy(shifts = it.shifts.filter { s -> s.id != second.id }.map { s -> if (s.id == first.id) restored else s }) }
        }
        viewModelScope.launch {
            useCase(workplaceId, rosterId, shiftId, editor).fold(
                onSuccess = { outcome ->
                    refreshInputs()
                    _uiState.update {
                        it.copy(
                            shifts = it.shifts.filter { s -> s.id != outcome.removedId }.map { s -> if (s.id == outcome.restored.id) outcome.restored else s },
                            records = outcome.record?.let { record -> it.records + (shift.date to record) } ?: it.records,
                            message = UiText.Resource(Res.string.split_undone)
                        )
                    }
                },
                onFailure = { e ->
                    _uiState.update { it.copy(shifts = before) }
                    rollBack(shift.date, beforeRecord, e)
                }
            )
        }
    }

    /**
     * SH-128: [event]'s day recomputed from the typed takings and target with the month's loaded
     * inputs (nothing is read or saved); figures equal to the saved ones drop the preview.
     */
    private fun preview(event: DailyRecordsEvent.Preview) {
        val current = _uiState.value.records[event.date] ?: return
        val inputs = inputs ?: return
        val netto = event.netto
        val plan = event.plan ?: current.revenue.planNetto
        if (netto == current.revenue.realNetto && plan == current.revenue.planNetto) {
            _uiState.update { it.copy(previews = it.previews - event.date) }
            return
        }
        val record = calculateDailyRecordUseCase.compute(
            workplaceId, rosterId, event.date, inputs,
            DailyRevenueInput(
                actualBrutto = current.revenue.realBrutto,
                actualNetto = netto,
                planNetto = plan,
                comments = current.revenue.comments
            )
        ).copy(isLocked = current.isLocked)
        _uiState.update { it.copy(previews = it.previews + (event.date to record)) }
    }

    /** SH-257: the month was read once (a refresh then reads it again; before, the first load does). */
    private var loaded = false

    /** SH-257: the local time now, on the screen's today ([today]); its end when the clock is on another day. */
    private fun localNow(): String {
        val day = today()
        val local = AppClock.now().toLocalDateTime(TimeZone.currentSystemDefault())
        return if (local.date == day) local.toString().let { if (it.length == 16) "$it:00" else it.take(19) } else "${day}T23:59:59"
    }

    /**
     * SH-257: the month's shifts and stored days read again (confirmed hours change the actual
     * labour), keeping the days on screen; a failed read keeps what is shown.
     */
    private fun refreshShifts() {
        if (!loaded) return
        viewModelScope.launch {
            val shifts = shiftRepository?.getShiftsForRoster(rosterId)?.getOrNull()
            val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrNull()
            _uiState.update { state ->
                state.copy(
                    shifts = shifts?.let { PersonNames.named(it, state.names) } ?: state.shifts,
                    records = stored?.let { list -> state.records + list.associateBy { it.date } } ?: state.records,
                    nowLocal = localNow()
                )
            }
            refreshInputs()
        }
    }

    /** SH-128: the month's inputs again after a change of shifts or costs (for the previews). */
    private fun refreshInputs() {
        viewModelScope.launch {
            calculateDailyRecordUseCase.load(workplaceId, rosterId).getOrNull()?.let { loaded ->
                inputs = loaded
                _uiState.update { it.copy(ledger = loaded.fixedCosts) }
            }
        }
    }

    /**
     * SH-120: optimistic: every pending suggestion shows as its day's target at once (with its
     * planned labour %), then the days are saved one by one; a day that fails goes back and says why.
     */
    private fun acceptSuggestedTargets() {
        val pending = _uiState.value.pendingSuggestions
        if (pending.isEmpty()) return
        val before = pending.associate { s -> s.date.toString() to _uiState.value.records.getValue(s.date.toString()) }
        _uiState.update { state ->
            state.copy(records = state.records + pending.associate { s -> s.date.toString() to before.getValue(s.date.toString()).withTarget(s.amount) })
        }
        viewModelScope.launch {
            var saved = 0
            for (s in pending) {
                val date = s.date.toString()
                updateDailyTargetUseCase(workplaceId, rosterId, date, s.amount).fold(
                    onSuccess = { record ->
                        saved++
                        _uiState.update { it.copy(records = it.records + (date to record)) }
                    },
                    onFailure = { e -> rollBack(date, before[date], e) }
                )
            }
            if (saved > 0) _uiState.update { it.copy(message = UiText.Resource(Res.string.forecast_accepted, saved)) }
        }
    }

    /** SH-148: a day's figures in [saves]: one write after another, never cut off by leaving. */
    private fun dayKey(date: String) = "daily:$rosterId:$date"

    /** Undoes an optimistic change of one day whose save failed, and says why. */
    private fun rollBack(date: String, before: DailyRecord?, e: Throwable) {
        _uiState.update {
            it.copy(
                records = if (before != null) it.records + (date to before) else it.records - date,
                error = e.toUiText()
            )
        }
    }
}

/** The day with planned revenue [target] (net) and its planned labour % recalculated against it. */
internal fun DailyRecord.withTarget(target: Double): DailyRecord {
    val planned = laborStats.planned
    return copy(
        revenue = revenue.copy(planNetto = target),
        laborStats = laborStats.copy(
            planned = planned.copy(laborPercentage = if (target > 0) planned.totalCost / target * 100 else 0.0)
        )
    )
}
